import {useMutation, useQueryClient} from '@github-ui/react-query'
import {PageData} from '@github-ui/pull-request-page-data-tooling/page-data'
import {usePageDataUrl} from '@github-ui/pull-request-page-data-tooling/use-page-data-url'
import {descriptionQueryKey} from '../page-data/loaders/use-description-data'
import {
  fetchWithErrorHandling,
  parseJSONWithBetterErrors,
  throwErrorsIfBadResponse,
} from '@github-ui/pull-request-page-data-tooling/fetch-error-handling'

/**
 * Updates the description (body) of a pull request. On success, will update Tanstack query data
 * for the pull request description to reflect the new description.
 *
 * @example
 * ```ts
 * const {mutate} = useUpdateDescriptionMutation()
 * mutate({id: pullRequest.id, body: 'new description'})
 * ```
 */
export function useUpdateDescriptionMutation(onError?: (error: Error) => void) {
  const queryClient = useQueryClient()
  const url = usePageDataUrl(PageData.updateDescription)
  const descriptionURL = usePageDataUrl(PageData.description)
  const descriptionKey = descriptionQueryKey(descriptionURL)

  return useMutation({
    networkMode: 'always',
    mutationFn: async ({id, body}: {id: number; body: string}) => {
      const response = await fetchWithErrorHandling(url, {method: 'PATCH', body: {id, body}})

      const json = await parseJSONWithBetterErrors(response)
      throwErrorsIfBadResponse(response, json)

      return json
    },
    onSuccess: async result => {
      queryClient.setQueryData(descriptionKey, result)
    },
    onError,
  })
}
